import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TAGLINE, COMPASS_ICON_URL } from "@/lib/brand";

export default function AboutUs() {
  return (
    <div>
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 pt-16 md:pt-20 pb-14">
          <div className="eyebrow mb-6">About Us — Our Philosophy</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink max-w-4xl">
            Good advice starts with<br />
            <span className="italic text-brass-deep">education</span>, not execution.
          </h1>
        </div>
      </section>

      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="eyebrow">Our belief</div>
              <p className="font-display text-[22px] md:text-[24px] leading-snug text-ink mt-4">
                Our role isn't simply to sell you a mutual fund or an insurance policy — it's to help
                you understand why it fits into your bigger financial picture, and how it moves you
                closer to the life you're planning for, while protecting you from the things you
                can't predict.
              </p>
            </div>
            <div className="md:col-span-7 space-y-6">
              <p className="text-[16px] text-ink-soft leading-relaxed">
                We take a genuinely holistic view of your finances — not two separate services, but
                one strategy built on two things happening together:
              </p>

              <div className="grid gap-4">
                <PillarCard
                  num="01"
                  label="Building"
                  body="Goal-based investing for the outcomes you want: a home, your child's education and marriage, a comfortable retirement, and the travel and experiences that make life worth living."
                />
                <PillarCard
                  num="02"
                  label="Protecting"
                  body="The right cover for the exigencies you don't want and can't foresee: the loss of a bread earner, a permanent disability, or the loss of a job or business."
                />
              </div>

              <p className="text-[15.5px] text-ink leading-relaxed">
                Standing between you and both is <span className="text-brass-deep font-medium">inflation</span> —
                a constant headwind that makes tomorrow's goals costlier and today's protection
                insufficient if left unreviewed. Everything we do is designed with this full picture
                in mind, not just one piece of it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="card-paper p-10 md:p-14 ledger-rule">
            <div className="eyebrow mb-4">The tagline we live by</div>
            <p className="font-display text-3xl md:text-4xl lg:text-5xl text-ink leading-[1.15]">
              {TAGLINE}
            </p>
            <div className="mt-8">
              <a
                href="https://reliservepulse.com/book-consultation"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-brass"
                data-testid="about-cta"
              >
                Begin the Conversation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ---- About Rohan Ghotage ---- */}
      <section className="border-b hairline bg-paper-raised">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-4">
              <div className="w-40 h-40 md:w-52 md:h-52 rounded-full bg-paper border hairline flex items-center justify-center overflow-hidden">
                <span className="font-display text-6xl md:text-7xl text-brass-deep">RG</span>
              </div>
              <div className="mt-6 space-y-2" data-testid="rohan-badges">
                <Badge>B.E., University of Pune</Badge>
                <Badge>MBA, Thunderbird School of Global Management</Badge>
                <Badge>AMFI-Registered MFD (ARN: 369518)</Badge>
              </div>
            </div>
            <div className="md:col-span-8" data-testid="rohan-bio">
              <div className="eyebrow mb-3">About the founder</div>
              <h2 className="font-display text-3xl md:text-4xl text-ink leading-tight">
                Rohan Ghotage
              </h2>
              <div className="mt-6 space-y-5 text-[15.5px] text-ink-soft leading-relaxed">
                <p>
                  Rohan Ghotage brings over 16 years of diverse business experience across multiple
                  industries, combined with more than a decade of personal investing experience, to
                  every conversation he has with clients.
                </p>
                <p>
                  A Bachelor of Engineering graduate from the <strong className="text-ink">University of Pune</strong>,
                  Rohan went on to earn his MBA from the <strong className="text-ink">Thunderbird School of Global
                  Management</strong> in Arizona, USA — one of the world's leading institutions for
                  international business education. That combination — engineering rigor paired with
                  a global business perspective — shapes how he approaches financial planning:
                  methodically, analytically, and always with an eye on the bigger picture.
                </p>
                <p>
                  Over the course of his career, Rohan has worked across various industries, giving
                  him a vantage point most financial advisors simply don't have — a genuine,
                  hands-on understanding of how businesses actually operate, generate revenue, and
                  create value. It isn't theoretical knowledge; it's the accumulated judgment of
                  someone who has spent over 16 years navigating real business environments.
                </p>
                <p>
                  As an active investor for over a decade, Rohan doesn't advise from the sidelines —
                  he applies the same principles to his own portfolio that he recommends to clients.
                  He is an AMFI-registered and certified Mutual Fund Distributor, grounded in a
                  philosophy of <em>strong fundamental research and long-term value investing</em>.
                  Rather than chasing trends or timing markets, Rohan believes in identifying
                  quality, understanding what you own, and staying disciplined through market cycles —
                  principles that guide both his own investing journey and the advice he gives
                  clients today.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---- Compass teaser ---- */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <Link
            to="/compass"
            className="card-paper block p-8 md:p-10 group"
            data-testid="about-compass-teaser"
          >
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-2 flex md:justify-center">
                <img src={COMPASS_ICON_URL} alt="Reliserve Wealth Compass" className="w-20 h-20 md:w-24 md:h-24" />
              </div>
              <div className="md:col-span-8">
                <div className="eyebrow mb-2">Reliserve Wealth Compass</div>
                <h3 className="font-display text-2xl md:text-3xl text-ink leading-snug">
                  We run our financial education initiative.
                </h3>
                <p className="text-ink-soft text-[15px] leading-relaxed mt-3">
                  Compass brings seminars and webinars to students, professionals, and
                  organizations — built to educate rather than sell.
                </p>
              </div>
              <div className="md:col-span-2 md:text-right">
                <span className="link-brass inline-flex items-center gap-1.5 text-[14px]">
                  Learn more <ArrowRight size={14} />
                </span>
              </div>
            </div>
          </Link>
        </div>
      </section>
    </div>
  );
}

function Badge({ children }) {
  return (
    <div className="inline-flex items-center px-3 py-1.5 rounded-full border hairline text-[11.5px] text-ink-soft mr-2">
      {children}
    </div>
  );
}

function PillarCard({ num, label, body }) {
  return (
    <div className="card-paper p-6 flex gap-6 items-start">
      <div className="num text-brass-deep text-[14px] pt-1">{num}</div>
      <div>
        <div className="eyebrow">{label}</div>
        <p className="text-[14.5px] text-ink-soft leading-relaxed mt-2">{body}</p>
      </div>
    </div>
  );
}
